import { type RefObject, useEffect, useRef } from 'react';

import { getGoogleFontsCss2Href } from '~/hooks/useFontLoader';

/** Marks the preview loader's injected `<head>` links so tests (and debugging humans) can find them. */
export const FONT_PREVIEW_LOADER_ATTRIBUTE = 'data-font-preview-loader';

/**
 * Families per css2 request — batches the families one IntersectionObserver callback delivers
 * while keeping each URL comfortably under practical URL-length limits.
 */
const FAMILIES_PER_REQUEST = 20;

/** Families already requested this session — reopening a picker must never refetch. */
const requestedFontFamilies = new Set<string>();

/** Unique characters across the given labels, sorted so equal inputs build a stable (cacheable) URL. */
export const getPreviewSubsetText = (labels: string[]): string => [...new Set(labels.join(''))].sort().join('');

/**
 * Injects glyph-subset Google Fonts stylesheets for the given built-in families, deduped for the
 * document's lifetime. Each css2 request is subset via `text=` to the characters of the batched
 * family names (a superset of each label's own characters), so a whole batch costs one small
 * stylesheet plus a ~1KB woff2 per family — never the families' full character sets. The links are
 * deliberately never cleaned up: they're idempotent, tiny, and removing them on picker close would
 * refetch on every reopen.
 */
export const loadFontFamilyPreviews = (fontFamilies: string[]): void => {
  const newFamilies = fontFamilies.filter((fontFamily) => !requestedFontFamilies.has(fontFamily));

  for (const fontFamily of newFamilies) {
    requestedFontFamilies.add(fontFamily);
  }

  for (let index = 0; index < newFamilies.length; index += FAMILIES_PER_REQUEST) {
    const batch = newFamilies.slice(index, index + FAMILIES_PER_REQUEST);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = getGoogleFontsCss2Href({
      families: batch.map((fontFamily) => ({ fontFamily })),
      text: getPreviewSubsetText(batch),
    });
    link.setAttribute(FONT_PREVIEW_LOADER_ATTRIBUTE, 'true');
    document.head.appendChild(link);
  }
};

const fontFamilyByElement = new WeakMap<Element, string>();

let sharedObserver: IntersectionObserver | undefined;

/**
 * One observer for every font option on the page: its callback naturally batches all the options
 * that scroll into view in the same frame into a single css2 request.
 */
const getSharedObserver = (): IntersectionObserver => {
  sharedObserver ??= new IntersectionObserver((entries) => {
    const visibleFamilies: string[] = [];

    for (const entry of entries) {
      if (!entry.isIntersecting) {
        continue;
      }

      // A family only needs to load once — stop watching the element as soon as it's been seen.
      sharedObserver?.unobserve(entry.target);

      const fontFamily = fontFamilyByElement.get(entry.target);

      if (fontFamily) {
        visibleFamilies.push(fontFamily);
      }
    }

    if (visibleFamilies.length > 0) {
      loadFontFamilyPreviews(visibleFamilies);
    }
  });

  return sharedObserver;
};

/**
 * Lazily loads a built-in (Google Fonts) family when the returned ref's element scrolls into view,
 * so font-picker option labels can render in their own typeface without eagerly fetching a font
 * file per option in the list. Attach the ref to the option's label element and style that element
 * with the family (plus a fallback) — the glyphs swap in when the subset stylesheet lands
 * (`display=swap`).
 *
 * The font list isn't virtualized, so every filtered option mounts at once — IntersectionObserver
 * (rather than load-on-mount) is what keeps a long unfiltered list from fetching hundreds of font
 * files the user never scrolls to.
 */
export const useFontPreviewWhenVisible = (fontFamily: string): RefObject<HTMLSpanElement> => {
  const elementRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const element = elementRef.current;

    if (!element || requestedFontFamilies.has(fontFamily)) {
      return;
    }

    fontFamilyByElement.set(element, fontFamily);
    const observer = getSharedObserver();
    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [fontFamily]);

  return elementRef;
};
